/**
 * Whose Queue a response is: the whole organisation's for an administrator, or the same
 * narrowed view for HR and IT. The server/web parity is pinned by
 * `server/tests/access.queue-cache-scope.test.ts`. Other actors never reach this read.
 */
export type AccessQueueScope = 'admin' | 'limited';

export function accessQueueScopeOf(actor: { isAdmin: boolean }): AccessQueueScope {
  return actor.isAdmin ? 'admin' : 'limited';
}

/** All TanStack Query keys in one place, so no cache key is spelled twice. */
export const queryKeys = {
  me: ['me'] as const,
  branding: ['branding'] as const,
  /** The signed-in person's OWN launcher list. Never keyed by a user id: there is no such
   *  parameter on the endpoint, and a key that carried one would imply there could be. */
  myApplications: ['me', 'applications'] as const,
  /** The launcher's announcements — ACTIVE only, same no-user-parameter rule as above. */
  myAnnouncements: ['me', 'announcements'] as const,
  /** The signed-in person's OWN two-step state — never anyone else's; no user parameter. */
  my2fa: ['me', '2fa'] as const,
  /** The signed-in person's OWN devices — same no-user-parameter rule as the two above. */
  mySessions: ['me', 'sessions'] as const,
  /** The sign-in page's continue-to lookup, keyed by the untrusted value it asked about. */
  redirectTarget: (redirect: string) => ['redirect-target', redirect] as const,
  users: ['admin', 'users'] as const,
  /** The whole live set, grouped by person — unpaginated; search is client-side. */
  adminSessions: ['admin', 'sessions'] as const,
  applications: ['admin', 'applications'] as const,
  /** The admin list: EVERY announcement, active and retired. Unpaginated — the whole set. */
  announcements: ['admin', 'announcements'] as const,
  /** The managed announcement types, live and retired. Unpaginated — the whole small set. */
  announcementTypes: ['admin', 'announcement-types'] as const,
  /** The managed launcher tag vocabulary, live and retired. Unpaginated, same reason. */
  applicationTags: ['admin', 'application-tags'] as const,
  /* There is deliberately no colour-settings key: an application's pill colour is a column
     on the application row (U2), so it rides `applications` and the reads that join it —
     `announcements`, `myAnnouncements`, `adminSessions`, `mySessions` — never a key of its
     own. A second cache entry for it would be a second home for one fact. */
  /** Health-poll cadence, failure tolerance and the rate limits — Ruling 1 plus U5. */
  operationalSettings: ['admin', 'settings', 'operational'] as const,
  /** Mail settings and the effective setup (SIR-7804). */
  emailSettings: ['admin', 'settings', 'email'] as const,
  /** The access module's settings (SIR-7804 shows them on Settings → Access). */
  accessSettings: ['admin', 'access', 'settings'] as const,
  grantsForUser: (userId: string, includeRevoked: boolean) =>
    ['admin', 'grants', 'user', userId, includeRevoked] as const,
  grantsForApplication: (applicationId: string, includeRevoked: boolean) =>
    ['admin', 'grants', 'application', applicationId, includeRevoked] as const,
  /** Coarse invalidation target — every grant list, whichever side it was keyed from. */
  allGrants: ['admin', 'grants'] as const,
  /*
   * Access wave 1. Every access read sits under ONE prefix, `accessAll`, so a mutation
   * anywhere in the module (a decision, a record edit, a catalog change) invalidates every
   * view that could show it — the Queue's counts, the person page, the Matrix — and no
   * screen is left showing a stale count beside a fresh list.
   */
  accessAll: ['admin', 'access'] as const,
  accessLevels: ['admin', 'access', 'levels'] as const,
  accessNodes: (includeRetired: boolean) => ['admin', 'access', 'nodes', includeRetired] as const,
  accessCatalog: (includeRetired: boolean) =>
    ['admin', 'access', 'catalog', includeRetired] as const,
  accessRecords: (filters: { userId?: string; catalogItemId?: string; includeRemoved: boolean }) =>
    ['admin', 'access', 'records', filters] as const,
  accessRequests: (filters: { status: string; userId?: string }) =>
    ['admin', 'access', 'requests', filters] as const,
  /*
   * THE QUEUE IS KEYED BY WHOSE QUEUE IT IS. The server answers the same URL with every row
   * for an administrator and only "HR can grant directly" rows for HR, so a key without the
   * scope would let an admin-wide answer be read back under an HR session — an admin+HR
   * person who demotes themself would keep seeing rows, counts and bell titles that are no
   * longer theirs until a refetch happened to land. With the scope in the key, the fresh
   * session reads a different entry and the admin-wide one is never served to it.
   * `accessQueueScope(scope)` is the prefix of every Queue entry for that scope, filtered or
   * not, so one call can drop them all.
   */
  accessQueueScope: (scope: AccessQueueScope) => ['admin', 'access', 'queue', scope] as const,
  accessQueue: (
    scope: AccessQueueScope,
    filters: { type?: string; nodeId?: string; userId?: string; q?: string },
  ) => ['admin', 'access', 'queue', scope, filters] as const,
  /** One target's audit rows — an access record's or catalog row's History. */
  accessHistory: (targetId: string) => ['admin', 'access', 'history', targetId] as const,
  /*
   * Spec rows 13 and 15, under the same `accessAll` prefix so every access mutation
   * refreshes them too. Other requests are an administrator's alone (HR gets a 403), so their
   * key needs no scope. IT emails answer the same for an administrator and for HR (both see
   * every email), so they need none either.
   */
  accessOtherRequests: (status: string) => ['admin', 'access', 'other-requests', status] as const,
  itEmails: ['admin', 'access', 'it-emails'] as const,
  itEmailMemberships: ['admin', 'access', 'it-email-memberships'] as const,
  /** The admin Emails page: every answer email in the outbox, one page at a time. */
  adminMail: (page: { page: number; pageSize: number }) => ['admin', 'mail', page] as const,
  /** The signed-in person's OWN requestable catalog and requests — no user parameter. */
  myAccessCatalog: ['me', 'access', 'catalog'] as const,
  myAccessRequests: ['me', 'access', 'requests'] as const,
  myOtherRequests: ['me', 'access', 'other-requests'] as const,
  /** The answer emails of one of the person's OWN requests (History on the launcher). */
  myRequestEmails: (kind: 'catalog' | 'other', id: string) =>
    ['me', 'access', 'emails', kind, id] as const,
  /**
   * The bell: the person's OWN notifications plus the unread and request counts. The rail
   * and the Queue's red count read `meta.accessRequestCount` from this same entry, so the
   * bell, the rail and the sub-nav can never disagree.
   */
  myNotifications: ['me', 'notifications'] as const,
  /**
   * The audit read, keyed by the filters the SERVER resolves plus the page. Every one of
   * them changes which rows come back, so each combination is its own cache entry — a key
   * that dropped one would serve the previous filter's rows under the new filter's count,
   * or one page's rows under another page's heading.
   */
  audit: (filters: {
    group: string;
    q: string;
    from: string;
    to: string;
    page: number;
    /** Rows per page — the `auditPageSize` operational setting. Page 2 of 50 and page 2 of
     *  100 are different row sets, so the size is part of the key like the page is. */
    limit: number;
  }) => ['admin', 'audit', filters] as const,
};
